// ============================================================================
// SETTINGS PAGE - With i18n Language Selection
// ============================================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, Globe, User, Bell, Shield, CreditCard, 
  ChevronRight, Check, LogOut
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { LANGUAGES } from '../i18n';

const Settings = () => {
  const { t, i18n } = useTranslation();
  const { user, profile, signOut } = useAuth();
  const [activeSection, setActiveSection] = useState('language');
  
  const isRTL = i18n.dir() === 'rtl';
  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('lab2040_language', langCode);
    
    // Update document direction
    const lang = LANGUAGES.find(l => l.code === langCode);
    document.documentElement.dir = lang?.dir || 'ltr';
    document.documentElement.lang = langCode;
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/dashboard" 
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className={`w-5 h-5 text-gray-600 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
              <h1 className="font-serif text-xl font-bold text-gray-900">{t('nav.settings')}</h1>
            </div>
            <Link to="/" className="font-serif text-xl font-bold">
              <span className="text-gray-900">Lab</span>
              <span className="text-teal-500">2040</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <nav className="space-y-1">
              {[
                { id: 'language', icon: Globe, label: 'Language' },
                { id: 'account', icon: User, label: 'Account' },
                { id: 'billing', icon: CreditCard, label: t('subscription.manageBilling') },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-start ${
                    activeSection === item.id
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">{t('nav.logout')}</span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {/* Language Settings */}
            {activeSection === 'language' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="font-serif text-lg font-bold text-gray-900">{t('language.selectLanguage')}</h2>
                  <p className="text-sm text-gray-500 mt-1">Choose your preferred language for the interface</p>
                </div>
                <div className="p-6">
                  <div className="grid gap-3">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                          currentLang.code === lang.code
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-3xl">{lang.flag}</span>
                        <div className="flex-1 text-start">
                          <p className={`font-semibold ${
                            currentLang.code === lang.code ? 'text-teal-600' : 'text-gray-900'
                          }`}>
                            {lang.nativeName}
                          </p>
                          <p className="text-sm text-gray-500">{lang.name}</p>
                        </div>
                        {currentLang.code === lang.code && (
                          <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        )}
                        {lang.dir === 'rtl' && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">RTL</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Account Settings */}
            {activeSection === 'account' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="font-serif text-lg font-bold text-gray-900">Account</h2>
                  <p className="text-sm text-gray-500 mt-1">Manage your account settings</p>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <div className="px-4 py-3 bg-gray-100 rounded-xl text-gray-600">
                      {user?.email || 'Not logged in'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('subscription.status')}</label>
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                      profile?.subscription_status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {profile?.subscription_status === 'active' ? (
                        <>
                          <Check className="w-4 h-4" />
                          {t('subscription.proMember')}
                        </>
                      ) : (
                        t('subscription.free')
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Billing Settings */}
            {activeSection === 'billing' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="font-serif text-lg font-bold text-gray-900">{t('subscription.manageBilling')}</h2>
                  <p className="text-sm text-gray-500 mt-1">Manage your subscription and payment methods</p>
                </div>
                <div className="p-6">
                  {profile?.subscription_status === 'active' ? (
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-amber-900">{t('subscription.proMonthly')}</p>
                            <p className="text-sm text-amber-700">$6.99/month</p>
                          </div>
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                            {t('subscription.active')}
                          </span>
                        </div>
                      </div>
                      
                      <a
                        href="https://billing.stripe.com/p/login/4gM7sMcdU6dN1UP0YE4Rq00"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors"
                      >
                        {t('subscription.manageBilling')}
                      </a>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600 mb-4">You're currently on the free plan</p>
                      <Link
                        to="/results"
                        className="inline-block px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-600 text-white rounded-xl hover:from-teal-700 hover:to-teal-700 transition-colors"
                      >
                        {t('subscription.upgradeToPro')}
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
